<template>
  <section v-if="highlights.length" id="highlights" class="highlights">
    <ImageContentful
      v-for="(highlight, index) in highlights"
      :key="`bg-${index}`"
      :src="highlight.imageUrl"
      :isBackgroundImage="true"
      :lazyload="true"
      :options="contentfulOptions"
      :class="[
        'highlights__background',
        `highlights__background--${renderValueOrDefault(
          highlight.backgroundAlignment,
          BACKGROUND_ALIGNMENT_OPTIONS
        )}`,
        `highlights__background--${renderValueOrDefault(
          highlight.backgroundSize,
          BACKGROUND_SIZE_OPTIONS
        )}`,
        { 'highlights__background--open': index === expandedHighlightIndex },
      ]"
      :style="{ backgroundColor: highlight.backgroundColor }"
    />
    <div class="highlights__block-wrap">
      <div
        v-for="(highlight, index) in highlights"
        :key="`copy-${index}`"
        :class="[
          `highlights__block highlights__block--section${index}`,
          `highlights__block--${
            index === expandedHighlightIndex
              ? 'open'
              : `closed-with-section${expandedHighlightIndex}`
          }`,
        ]"
        v-data-test-id="`highlights-block-${index}`"
      >
        <transition name="fade">
          <h3
            v-show="index !== expandedHighlightIndex"
            ref="label"
            :class="[
              'highlights__label',
              `highlights__label--section${index}`,
              {
                'highlights__label--section1-2nd-pos': index === 1 && tab1Has2ndPosition,
                'highlights__label--static': index === 1 && tab1IsStatic,
              },
            ]"
            v-data-test-id="`highlights-label-${index}`"
            @mouseenter="expandHighlight($event, index)"
            @click="expandHighlight($event, index, highlight)"
          >
            {{ highlight.label }}
          </h3>
        </transition>
        <transition name="slide-and-appear">
          <div
            v-show="index === expandedHighlightIndex"
            :class="`highlights__details highlights__details--section${index}`"
            v-data-test-id="`highlights-details-${index}`"
          >
            <h4 class="highlights__details-label">
              {{ highlight.label }}
            </h4>
            <h4 class="highlights__title">
              {{ highlight.title }}
            </h4>
            <div class="highlights__description">
              {{ highlight.description }}
            </div>
          </div>
        </transition>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import ImageContentful from '@/components/_shared/ImageContentful.vue'
import {
  notifyExternalServices,
  ACTIONS,
} from '@/services/external-communication/external-communication'

const BACKGROUND_ALIGNMENT_OPTIONS = {
  left: 'left',
  center: 'center',
  right: 'right',
}
BACKGROUND_ALIGNMENT_OPTIONS.default = BACKGROUND_ALIGNMENT_OPTIONS.center

const BACKGROUND_SIZE_OPTIONS = {
  fullHeight: 'full-height',
  cover: 'cover',
}
BACKGROUND_SIZE_OPTIONS.default = BACKGROUND_SIZE_OPTIONS.cover

const CONTENTFUL_SETTINGS = {
  desktop: {
    imageCompression: 61,
    retinaSupportFactor: 0,
    dimensionCorrectionFactor: 0.6,
  },

  mobile: {
    imageCompression: 71,
    retinaSupportFactor: 0.3,
    dimensionCorrectionFactor: 1,
  },
}

export default {
  name: 'Highlights',
  components: {
    ImageContentful,
  },

  data() {
    return {
      headerHeight: 0,
      oldExpandedHighlightIndex: -1,
      expandedHighlightIndex: 0,
      BACKGROUND_ALIGNMENT_OPTIONS,
      BACKGROUND_SIZE_OPTIONS,
    }
  },

  computed: {
    ...mapGetters({
      highlights: 'product/currentHighlights',
      isMqLarger: 'viewport/isMqLarger',
      product: 'product/product',
      vpWidth: 'viewport/width',
    }),

    tab1Has2ndPosition() {
      return this.expandedHighlightIndex === 0 || this.oldExpandedHighlightIndex === 0
    },

    tab1IsStatic() {
      return this.expandedHighlightIndex !== 1 && this.oldExpandedHighlightIndex !== 1
    },

    contentfulOptions() {
      return this.getContentfulOptions(CONTENTFUL_SETTINGS[this.isMqLarger ? 'desktop' : 'mobile'])
    },
  },

  watch: {
    isMqLarger: {
      immediate: true,
      handler() {
        this.headerHeight = document.querySelector('header')?.offsetHeight || 0
      },
    },
  },

  methods: {
    expandHighlight($event, index, highlight) {
      if (!this.isMqLarger) this.scrollToHighlight(index)

      this.oldExpandedHighlightIndex = this.expandedHighlightIndex
      this.expandedHighlightIndex = index
      if ($event.type === 'click')
        notifyExternalServices({
          action: ACTIONS.productInteraction,
          details: { action: 'show_highlight', label: highlight.type },
        })
    },

    scrollToHighlight(index) {
      const tabHeight = this.$refs.label[index].offsetHeight
      const scrollSpaceToHighlight =
        this.$el.getBoundingClientRect().top - this.headerHeight + tabHeight * index

      On.smoothScrollTo(window.pageYOffset + scrollSpaceToHighlight, {
        duration: On.TransitionTimes.medium,
        easing: 'easeInOutQuad',
      })
    },

    renderValueOrDefault(value, options) {
      return Object.values(options).includes(value) ? value : options.default
    },

    getContentfulOptions(settings) {
      return {
        imageCompression: settings.imageCompression,
        retinaSupportFactor: settings.retinaSupportFactor,
        fixedWidth: this.vpWidth * settings.dimensionCorrectionFactor,
        fixedHeight: this.vpWidth * settings.dimensionCorrectionFactor,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import './Highlights';
</style>
